import { defineStore } from "pinia";
import { SalesApi } from "../api/SalesApi";
import { SalesByFamiyResponse, ProductsByFamilyTimelineResponse, ProductsReportResponse } from "../responses";
import { colorsUtils } from "../utils/ColorsUtils";
import { Family } from "../models/Family";
import * as echarts from 'echarts';
import { useUserManagerStore } from "./userManagerStore";
import { dateUtils } from "../utils/DateUtils";
import { useAppSettingsStore } from "./AppSettingsStore";

export type bar = 'bar';

interface CenterReportState {
    // Define the state properties here
    center: number | null;
    reportId: string | null;
    families: Family[],
    rawResponse: SalesByFamiyResponse | null;
    productsReport: ProductsReportResponse | null;
    selectedFamily: string | null;
    familyProducts: any | null;
    familyProductsTimeline: ProductsByFamilyTimelineResponse | null;
    showingProducts: boolean;
    loadingProducts: boolean;
}

export const useCenterReportStore = defineStore('center-report', {
    state: (): CenterReportState => ({
        center: null,
        reportId: null,
        families: [],
        rawResponse: null,
        productsReport: null,
        selectedFamily: null,
        familyProducts: null,
        familyProductsTimeline: null,
        showingProducts: false,
        loadingProducts: false
    }),

    getters: {
        familiesSortedByCurrentTotal(): Family[] {
            return this.families.sort((a, b) => {
                return b.getTotal() - a.getTotal();
            });
        },

        familiesVisible(): Family[] {
            return this.families.filter(family => family.visible);
        },

        familiesInvisible(): Family[] {
            return this.families.filter(family => !family.visible);
        },

        familiesVisibleSortedByCurrentTotal(): Family[] {
            return this.familiesSortedByCurrentTotal.filter(family => family.visible);
        },

        familyNamesVisible(): string[] {
            return this.familiesVisibleSortedByCurrentTotal.map(family => family.label);
        },

        currentTotal(): number {
            return this.familiesVisible.reduce((acc, curr) => acc + curr.getTotal(), 0);
        },

        previousTotal(): number {
            return this.familiesVisible.reduce((acc, curr) => acc + curr.getTotal('previous'), 0);
        },

        doughnutData(): { labels: string[]; datasets: { label: string; data: number[]; backgroundColor: string[] }[] } {
            return {
                labels: this.familyNamesVisible,
                datasets: [
                    {
                        label: 'Sales',
                        data: this.familiesVisible.map(family => family.getTotal()),
                        backgroundColor: this.familiesVisible.map(family => family.color),
                    }
                ]
            }
        },

        barChartSeries(): echarts.BarSeriesOption[] {
            const dates = Object.keys(this.rawResponse?.current || {});
            const series = this.families.map((family) => {
                if (!family.visible) {
                    return null;
                }
                return {
                    name: family.label,
                    type: "bar" as bar,
                    stack: 'current',
                    data: dates.map(date => {
                        const amount = this.rawResponse?.current[date][family.label]?.included_tax ?? 0;
                        if (isNaN(amount)) {
                            return 0;
                        }

                        return amount / 100;
                    }),
                    itemStyle: {
                        color: family.color,
                    }
                }
            })

            return series.filter(series => series !== null) as echarts.BarSeriesOption[];
        },

        barChartXaxis(): any {
            const userManagerStore = useUserManagerStore();
            const reportType = userManagerStore.user?.components.find(component => component.id === this.reportId)?.config.type;
            const dates = Object.keys(this.rawResponse?.current || {});
            return dates.map(date =>
                dateUtils.formatDateForReportType(new Date(date), reportType)
            )

        },

        productsBarChartSeries(): echarts.BarSeriesOption[] {
            if (!this.familyProductsTimeline) {
                return [];
            }

            const appSettingsStore = useAppSettingsStore();
            const dates = Object.keys(this.familyProductsTimeline.current);
            
            // Extraire tous les noms de produits uniques avec leur total
            const productTotals = new Map<string, number>();
            dates.forEach(date => {
                Object.keys(this.familyProductsTimeline!.current[date]).forEach(productName => {
                    const productData = this.familyProductsTimeline!.current[date][productName];
                    if (productData) {
                        const amount = appSettingsStore.settings.taxIncluded 
                            ? productData.included_tax 
                            : productData.excluded_tax;
                        const currentTotal = productTotals.get(productName) || 0;
                        productTotals.set(productName, currentTotal + amount);
                    }
                });
            });

            // Filtrer et trier les produits par total décroissant (seulement ceux avec des ventes significatives)
            const sortedProductNames = Array.from(productTotals.entries())
                .filter(([_name, total]) => total > 0) // Filtrer les produits sans ventes
                .sort((a, b) => b[1] - a[1])
                .map(entry => entry[0]);

            const series = sortedProductNames.map((productName: string, index: number) => {
                return {
                    name: productName,
                    type: "bar" as bar,
                    stack: 'current',
                    data: dates.map(date => {
                        const productData = this.familyProductsTimeline!.current[date][productName];
                        if (productData) {
                            const amount = appSettingsStore.settings.taxIncluded 
                                ? productData.included_tax 
                                : productData.excluded_tax;
                            return amount / 100;
                        }
                        return 0;
                    }),
                    itemStyle: {
                        color: colorsUtils.getColor(index),
                    }
                }
            });

            return series;
        },

        productsDoughnutData(): { labels: string[]; datasets: { label: string; data: number[]; backgroundColor: string[] }[] } {
            if (!this.familyProducts) {
                return { labels: [], datasets: [] };
            }
            
            const appSettingsStore = useAppSettingsStore();
            const products = this.familyProducts.products || [];
            
            // Trier les produits par valeur décroissante
            const sortedProducts = [...products].sort((a: any, b: any) => {
                const amountA = appSettingsStore.settings.taxIncluded ? a.included_tax : a.excluded_tax;
                const amountB = appSettingsStore.settings.taxIncluded ? b.included_tax : b.excluded_tax;
                return amountB - amountA;
            });
            
            const labels = sortedProducts.map((product: any) => product.name);
            const data = sortedProducts.map((product: any) => {
                let amount = appSettingsStore.settings.taxIncluded ? product.included_tax : product.excluded_tax;
                return amount / 100;
            });
            const colors = sortedProducts.map((_: any, index: number) => colorsUtils.getColor(index));
            
            return {
                labels,
                datasets: [{
                    label: 'Products',
                    data,
                    backgroundColor: colors,
                }]
            };
        },

        productsSortedByValue(): any[] {
            if (!this.familyProducts) {
                return [];
            }
            
            const appSettingsStore = useAppSettingsStore();
            const products = this.familyProducts.products || [];
            
            // Trier les produits par valeur décroissante
            return [...products].sort((a: any, b: any) => {
                const amountA = appSettingsStore.settings.taxIncluded ? a.included_tax : a.excluded_tax;
                const amountB = appSettingsStore.settings.taxIncluded ? b.included_tax : b.excluded_tax;
                return amountB - amountA;
            });
        }
    },

    actions: {
        fetchProductsForFamily: async function (familyName: string) {
            if (!this.center || !this.reportId) {
                throw new Error('Center or Report ID is not set');
            }

            try {
                this.loadingProducts = true;
                this.selectedFamily = familyName;
                this.showingProducts = true; // Basculer immédiatement en mode produits pour afficher le loader
                
                // Réinitialiser les données des produits pour forcer le re-render
                this.familyProducts = null;
                this.familyProductsTimeline = null;
                
                // Lancer les deux requêtes en parallèle pour réduire le temps d'attente
                const [productsTimeline] = await Promise.all([
                    // SalesApi.fetchProductsForFamily(this.center, this.reportId, familyName),
                    SalesApi.fetchProductsTimelineForFamily(this.center, this.reportId, familyName)
                ]);
                
                // this.familyProducts = products;
                this.familyProductsTimeline = productsTimeline;
            } catch (error) {
                console.error('Error fetching products for family:', error);
                throw error;
            } finally {
                this.loadingProducts = false;
            }
        },

        showFamilies() {
            this.loadingProducts = false;
            this.showingProducts = false;
            this.selectedFamily = null;
            setTimeout(() => {
            // Retarder la réinitialisation des données pour éviter les transitions brusques
                this.familyProducts = null;
                this.familyProductsTimeline = null;
            }, 1000);
        },

        reset() {
            this.center = null;
            this.reportId = null;
            this.families = [];
            this.rawResponse = null;
            this.selectedFamily = null;
            this.familyProducts = null;
            this.familyProductsTimeline = null;
            this.showingProducts = false;
            this.loadingProducts = false;
        },
    },
})